import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Plus, Trash2, Send } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { PageHeader, ProductImage } from "../components/Bits";
import { useCart } from "../context/CartContext";
import { api, errMsg, money, calcTotals, lineNet } from "../lib/api";
import { DiscountTypeToggle } from "../components/Bits2";
import { CustomerPicker } from "../components/CustomerPicker";
import { useAuth } from "../context/AuthContext";

export const ProductPicker = ({ products, onAdd }) => {
  const [q, setQ] = useState("");
  const shown = useMemo(() => {
    const s = q.toLocaleLowerCase("tr");
    return products.filter((p) => !s || [p.name, p.code, p.category].some((v) => (v || "").toLocaleLowerCase("tr").includes(s))).slice(0, 60);
  }, [products, q]);
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="order-product-search-input" autoFocus placeholder="Ürün ara (ad, kod, kategori)..." value={q} onChange={(e) => setQ(e.target.value)} className="h-11 pl-10" />
        </div>
      </div>
      <div className="max-h-[60vh] divide-y divide-slate-100 overflow-y-auto">
        {shown.length === 0 && <p className="p-8 text-center text-sm text-slate-500" data-testid="order-product-search-empty">Ürün bulunamadı</p>}
        {shown.map((p) => (
          <button key={p.id} data-testid={`order-pick-product-${p.code}`} onClick={() => onAdd(p)}
            className="flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-blue-50">
            <ProductImage product={p} className="h-12 w-12 shrink-0 rounded-lg" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{p.name}</p>
              <p className="font-mono text-[11px] text-slate-500">{p.code} · Stok {p.stock} {p.unit}</p>
            </div>
            <span className="font-display text-sm font-bold">{money(p.price)}</span>
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-slate-950 text-white"><Plus className="h-4 w-4" /></span>
          </button>
        ))}
      </div>
    </div>
  );
};

const CartLine = ({ l, update, remove, noDisc }) => {
  const net = lineNet(l);
  const pct = l.discount_type !== "amount";
  return (
    <div data-testid={`cart-line-${l.code}`} className="flex gap-3 py-3">
      <ProductImage product={l} className="h-12 w-12 shrink-0 rounded-lg" />
      <div className="min-w-0 flex-1">
        <div className="flex justify-between gap-2">
          <p className="truncate text-sm font-semibold">{l.name}</p>
          <button data-testid={`cart-remove-${l.code}`} onClick={() => remove(l.product_id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
        </div>
        <p className="font-mono text-[11px] text-slate-500">{money(l.price)} / {l.unit} · KDV %{l.vat}</p>
        <div className="mt-2 flex items-end gap-2">
          <div className="w-20"><Label className="text-[10px] uppercase text-slate-500">Miktar</Label>
            <Input data-testid={`cart-qty-${l.code}`} type="number" min="1" step="1" value={l.quantity} onChange={(e) => update(l.product_id, { quantity: e.target.value })} className="h-9" /></div>
          {!noDisc && <div><Label className="text-[10px] uppercase text-slate-500">İskonto {pct ? "%" : "₺"}</Label>
            <div className="flex items-center gap-1">
              <Input data-testid={`cart-discount-${l.code}`} type="number" min="0" max={pct ? 100 : undefined} step={pct ? "0.5" : "0.01"} value={l.discount} onChange={(e) => update(l.product_id, { discount: e.target.value })} className="h-9 w-20" />
              <DiscountTypeToggle testId={`cart-discount-type-${l.code}`} value={l.discount_type || "percent"} onChange={(v) => update(l.product_id, { discount_type: v, discount: 0 })} />
            </div></div>}
          <p className="ml-auto font-display text-sm font-bold" data-testid={`cart-line-net-${l.code}`}>{money(net)}</p>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, id, strong }) => (
  <div className={`flex justify-between ${strong ? "pt-2 text-lg font-bold font-display" : "text-sm text-slate-600"}`}>
    <span>{label}</span><span data-testid={id}>{value}</span>
  </div>
);

export default function NewOrderPage() {
  const { lines, add, update, remove, clear } = useCart();
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const { user } = useAuth();
  const isCustomer = user.role === "customer";
  const [gd, setGd] = useState(0);
  const [gdType, setGdType] = useState("percent");
  const [c, setC] = useState({ customer_id: "", customer_name: "", customer_phone: "", customer_address: "", customer_tax_office: "", customer_tax_no: "", note: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  const t = isCustomer ? calcTotals(lines.map((l) => ({ ...l, discount: 0 })), 0) : calcTotals(lines, gd, gdType);
  const [credit, setCredit] = useState(null);
  useEffect(() => {
    setCredit(null);
    if (c.customer_id) api.get(`/customers/${c.customer_id}/credit`).then((r) => setCredit(r.data)).catch(() => {});
  }, [c.customer_id]);
  const overLimit = credit && credit.limit > 0 && credit.balance + t.total > credit.limit + 0.005;

  const submit = async () => {
    if (!isCustomer && !c.customer_name.trim()) return toast.error("Müşteri adı zorunludur");
    if (!lines.length) return toast.error("Siparişe en az bir ürün ekleyin");
    if (lines.some((l) => !(Number(l.quantity) > 0))) return toast.error("Miktarlar 0'dan büyük olmalı");
    if (overLimit) return toast.error("Müşterinin cari limiti aşılıyor. Sipariş oluşturulamaz.");
    setBusy(true);
    try {
      const { data } = await api.post("/orders", { ...c, ...(isCustomer ? { customer_name: user.name } : {}), general_discount: Number(gd) || 0, general_discount_type: gdType,
        items: lines.map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity), discount: isCustomer ? 0 : Number(l.discount) || 0, discount_type: l.discount_type || "percent" })) });
      toast.success(`${data.order_no} numaralı sipariş sisteme gönderildi`);
      clear();
      nav("/siparisler");
    } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Sipariş Oluştur" kicker={isCustomer ? "Ürün seç · Gönder" : "Ürün seç · İskonto uygula · Gönder"} />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="lg:col-span-7"><ProductPicker products={products} onAdd={(p) => { add(p); toast.success(`${p.name} eklendi`); }} /></div>
        <div className="lg:col-span-5 space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold">Sipariş Kalemleri <span className="text-slate-400">({lines.length})</span></h2>
              {lines.length > 0 && <button data-testid="cart-clear-button" onClick={clear} className="text-xs text-slate-500 hover:text-rose-600">Temizle</button>}
            </div>
            {lines.length === 0 ? <p data-testid="cart-empty" className="py-8 text-center text-sm text-slate-500">Soldan ürün arayıp ekleyin.</p>
              : <div className="divide-y divide-slate-100">{lines.map((l) => <CartLine key={l.product_id} l={l} update={update} remove={remove} noDisc={isCustomer} />)}</div>}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-3">
            <h2 className="font-display text-lg font-semibold">Müşteri Bilgileri</h2>
            {isCustomer ? (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900" data-testid="order-self-customer">
                Sipariş <b>{user.name}</b> hesabınıza oluşturulacak.
              </div>
            ) : <CustomerPicker value={c} onChange={setC} />}
            {credit && (
              <div data-testid="order-credit-info" className={`rounded-lg border p-3 text-sm ${overLimit ? "border-rose-300 bg-rose-50 text-rose-800" : "border-slate-200 bg-slate-50 text-slate-700"}`}>
                <p>Açık cari: <b>{money(credit.balance)}</b> · Limit: <b>{credit.limit > 0 ? money(credit.limit) : "Limitsiz"}</b></p>
                {overLimit && <p className="mt-1 font-semibold" data-testid="order-credit-warning">Cari limit aşılıyor! Bu siparişle bakiye {money(credit.balance + t.total)} olur. Sipariş oluşturulamaz.</p>}
              </div>
            )}
            <Textarea data-testid="order-note-input" placeholder="Sipariş notu" value={c.note} onChange={(e) => setC({ ...c, note: e.target.value })} />
          </div>
          <div className="rounded-xl bg-slate-950 p-5 text-white shadow-lg space-y-2 [&_span]:text-inherit">
            {!isCustomer && <div className="flex items-center justify-between pb-2">
              <Label className="text-sm text-slate-300">Genel İskonto {gdType === "percent" ? "%" : "₺"}</Label>
              <div className="flex items-center gap-2">
                <Input data-testid="order-general-discount-input" type="number" min="0" step="0.01" value={gd} onChange={(e) => setGd(e.target.value)} className="h-9 w-24 border-slate-700 bg-slate-900 text-white" />
                <DiscountTypeToggle dark testId="order-general-discount-type" value={gdType} onChange={(v) => { setGdType(v); setGd(0); }} />
              </div>
            </div>}
            <div className="space-y-1 text-slate-300 [&>div]:text-slate-300">
              <Row label="Brüt Toplam" value={money(t.gross)} id="order-total-gross" />
{!isCustomer && (<Row label="Satır İskontoları" value={`-${money(t.lineDisc)}`} id="order-total-line-discount" />)}
{!isCustomer && (<Row label="Genel İskonto" value={`-${money(t.genDisc)}`} id="order-total-general-discount" />)}
              <Row label="Ara Toplam" value={money(t.subtotal)} id="order-total-subtotal" />
              <Row label="KDV" value={money(t.vat)} id="order-total-vat" />
            </div>
            <div className="border-t border-slate-800 text-white"><Row label="Genel Toplam" value={money(t.total)} id="order-total-grand" strong /></div>
            <Button data-testid="order-submit-button" disabled={busy || overLimit} onClick={submit} className="mt-3 h-12 w-full bg-blue-600 text-base hover:bg-blue-500">
              <Send className="mr-2 h-4 w-4" /> {busy ? "Gönderiliyor..." : "Siparişi Gönder"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
